import { Button, RemoveRowButton } from '@library/components/buttons';
import { useCallback, useMemo, useRef, useState } from 'react';
import type { ImageFileTypes } from '@library/types/polls';
import { cn as classNames } from '@library/component-utils/cn';
import uploadCloudIcon from '@library/assets/images/icons/cloud-upload.svg';

export interface FileHandlerProps {
  fileType?: 'csv' | ImageFileTypes;
  onFileChange: (file: File | null) => void;
  onFileDrop: (file: File | null) => void;
  onRemoveFile: () => void;
  uploadFile: File | null;
}

export const FileHandler = ({
  fileType = 'csv',
  onFileChange,
  onFileDrop,
  onRemoveFile,
  uploadFile
}: FileHandlerProps) => {
  const [dragActive, setDragActive] = useState(false);
  const chooseFileInput = useRef<HTMLInputElement>(null);

  const handleChooseFileClick = useCallback(() => {
    chooseFileInput.current?.click();
  }, [chooseFileInput]);

  const handleDrag = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const { type } = event;

      if (type === 'dragenter' || type === 'dragover') {
        setDragActive(true);
      } else if (type === 'dragleave') {
        setDragActive(false);
      }
    },
    [setDragActive]
  );

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      setDragActive(false);

      const {
        dataTransfer: { files }
      } = event;

      onFileDrop(files[0]);
    },
    [onFileDrop, setDragActive]
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      onFileChange(files ? files[0] : null);
    },
    [onFileChange]
  );

  const handleRemoveFile = useCallback(() => {
    if (chooseFileInput.current) {
      const dataTransfer = new DataTransfer();
      chooseFileInput.current.files = dataTransfer.files;
    }

    onRemoveFile();
  }, [chooseFileInput, onRemoveFile]);

  const dragClassName: string = useMemo(() => {
    return classNames(
      dragActive
        ? 'bg-soFresh-130 border-soFresh-120'
        : 'bg-soFresh-120 border-soFresh-130',
      'border-dashed border-2 justify-center items-center relative h-48 text-black text-center w-full flex-col flex'
    );
  }, [dragActive]);

  return (
    <>
      <div className={dragClassName} onDragEnter={handleDrag}>
        {dragActive && (
          <div
            className="absolute z-20 size-full"
            data-testid="drag-active-overlay"
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          />
        )}
        <div>
          <img
            className="mx-auto pb-4"
            src={uploadCloudIcon}
            alt="Upload Icon"
          />
          <div className="flex text-lg leading-5">
            <input
              accept={`.${fileType}`}
              className="hidden"
              data-testid="input-file-upload"
              id="input-file-upload"
              onChange={handleFileChange}
              ref={chooseFileInput}
              type="file"
            />
            <Button
              className="text-soClean-100 font-bold"
              onClick={handleChooseFileClick}
              title="ChooseUploadFileButton"
            >
              Choose file
            </Button>
            <p className="text-soClean-180 p-2">or drag & drop file here...</p>
          </div>
        </div>
      </div>
      {!!uploadFile && (
        <div className="flex gap-4">
          <div className="bg-soFresh-120 flex h-10 w-full items-center justify-between rounded p-4">
            <span>{uploadFile.name}</span>
            <RemoveRowButton
              imageAltText="Remove File"
              onClick={handleRemoveFile}
            />
          </div>
        </div>
      )}
    </>
  );
};
